import { FormEvent, useState } from 'react';
import Alert from '@/components/Alert';
import Checkbox from '@/components/Checkbox';
import PageHeader from '@/components/PageHeader';
import ProductCategorySelectBox from '@/components/ProductCategorySelectBox';
import SearchForm from '@/components/search/SearchForm';
import useSearch from '@/components/search/useSearch';
import SupplierSelectBox from '@/components/SupplierSelectBox';
import { enableSkuSearchFacets } from '@/config';
import { ProductSearchFacetCode } from '@/models/Sku';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import AutoOrderButton from '@/modules/store/components/AutoOrderButton';
import FacetedProductCatalog from '@/modules/store/components/FacetedProductCatalog';
import ProductCatalogTable from '@/modules/store/components/ProductCatalogTable';
import ProductCatalogViewToggle, {
  ProductCatalogView,
} from '@/modules/store/components/ProductCatalogViewToggle';
import { useCart } from '@/modules/store/contexts/CartContext';
import { CartItemImpl } from '@/modules/store/models/CartItem';
import { ProductCategory } from '@/modules/store/models/ProductCategory';
import { Supplier } from '@/modules/store/models/Supplier';
import { CartPath } from '@/modules/store/paths';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Sku {
  id: string;
  supplierId: string;
}

const Shop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const storeId = currentUser().storeId;

  const { searchQuery, currentSearchQuery, page, handlePageChange, setSearchQuery } = useSearch();

  const supplierIdParam = searchParams.get('supplierId');
  const productCategoryNameParam = searchParams.get('category');
  const promotionsParam = searchParams.get('promotions') === 'yes';
  const supplierIdParams = searchParams.getAll('supplierId').filter(Boolean);
  const productCategoryIdParams = searchParams.getAll('productCategoryId').filter(Boolean);
  const catalogViewParam = searchParams.get('catalogView');
  const catalogView: ProductCatalogView =
    enableSkuSearchFacets && catalogViewParam === 'faceted' ? 'faceted' : 'classic';

  const { addToCart } = useCart();

  const updateSearchParams = (
    updates: Record<string, string | string[] | boolean | null | undefined>,
  ) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      nextParams.delete(key);

      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((item) => nextParams.append(key, item));
        return;
      }

      if (typeof value === 'boolean') {
        if (value) {
          nextParams.set(key, 'yes');
        }
        return;
      }

      if (value) {
        nextParams.set(key, value);
      }
    });

    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const search = (
    searchQuery: string | null,
    supplierId: string | null,
    productCategoryNameParam: string | null,
    promotions: boolean,
  ) => {
    updateSearchParams({
      query: searchQuery || '',
      supplierId: supplierId || '',
      category: productCategoryNameParam || '',
      promotions,
    });
  };

  const [supplier, setSupplier] = useState<Supplier | null>();
  const [productCategory, setProductCategory] = useState<ProductCategory | null>();
  const [promotions, setPromotions] = useState<boolean>(promotionsParam);

  const handleSupplierSelect = (item?: Supplier | null) => {
    if (item?.id === supplier?.id) {
      return;
    }

    setSupplier(item);

    if (item?.id === supplierIdParam) {
      return;
    }

    search(currentSearchQuery, item?.id || null, productCategory?.id || null, promotions);
  };

  const handleProductCategorySelect = (item?: ProductCategory | null) => {
    if (item?.id === productCategory?.id) {
      return;
    }

    setProductCategory(item);

    if (item?.id === productCategoryNameParam) {
      return;
    }

    search(
      currentSearchQuery,
      supplierIdParam, // item ? item?.supplierId || supplierIdParam : null,
      item?.id || null,
      promotions,
    );
  };

  const handlePromotionsChange = (value: boolean) => {
    if (value === promotions) {
      return;
    }

    setPromotions(value);

    if (value === promotionsParam) {
      return;
    }

    search(currentSearchQuery, supplierIdParam, productCategoryNameParam, value);
  };

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();

    updateSearchParams({ query: currentSearchQuery });
  };

  const toggleParamValue = (values: string[], value: string, checked: boolean): string[] => {
    if (checked) {
      return values.includes(value) ? values : [...values, value];
    }

    return values.filter((item) => item !== value);
  };

  const handleCatalogViewChange = (view: ProductCatalogView) => {
    updateSearchParams({ catalogView: view === 'faceted' ? 'faceted' : 'classic' });
  };

  const handleToggleFacetValue = (
    facetCode: ProductSearchFacetCode,
    value: string,
    checked: boolean,
  ) => {
    if (facetCode === 'supplier') {
      updateSearchParams({
        supplierId: toggleParamValue(supplierIdParams, value, checked),
      });
      return;
    }

    if (facetCode === 'category') {
      updateSearchParams({
        productCategoryId: toggleParamValue(productCategoryIdParams, value, checked),
      });
      return;
    }

    if (facetCode === 'promotion') {
      updateSearchParams({
        promotions: checked,
      });
    }
  };

  const handleClearFacets = () => {
    updateSearchParams({
      supplierId: [],
      productCategoryId: [],
      promotions: false,
    });
  };

  const handleOnAddToCart = (sku: Sku, quantity: number) => {
    addToCart(sku.supplierId, new CartItemImpl(sku.id, sku.supplierId, quantity));
  };

  const handleAutoOrderSuccess = () => {
    navigate(CartPath);
  };

  return (
    <div>
      <PageHeader
        title="Products"
        alert={
          <Alert
            type="info"
            message="&#128075; Welcome! How can we assist you today? It's great to have you here!"
          />
        }
      >
        <AutoOrderButton
          variant="success"
          loadingIconClassName="w-5 h-5 mr-1 fill-green-600"
          onSuccess={handleAutoOrderSuccess}
        >
          Auto-Order
        </AutoOrderButton>
      </PageHeader>

      <div className="flex flex-col xl:flex-row mb-10 xl:mb-8 gap-4">
        <div className="flex-1 flex-row">
          <SearchForm
            as="Fragment"
            value={searchQuery}
            onSubmit={handleSearchSubmit}
            onChange={setSearchQuery}
            placeholder="Search products"
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          {enableSkuSearchFacets && (
            <div className="flex items-end">
              <ProductCatalogViewToggle value={catalogView} onChange={handleCatalogViewChange} />
            </div>
          )}
          {catalogView === 'classic' && (
            <div className="flex flex-col md:flex-row gap-4 xl:grid xl:grid-cols-2">
              <div className="flex-1 lg:w-80">
                <SupplierSelectBox
                  value={supplierIdParam || null}
                  onSelect={handleSupplierSelect}
                />
              </div>
              <div className="flex-1 lg:w-80">
                <ProductCategorySelectBox<ProductCategory>
                  value={productCategoryNameParam || null}
                  onSelect={handleProductCategorySelect}
                  supplierId={supplier && supplier.id}
                  mapper={(item) => ({ ...item, id: item.name })}
                />
              </div>
              <div className="flex-1 lg:w-80 flex flex-row items-center gap-2 py-2 md:pt-8 xl:py-1">
                <Checkbox id="promotions" checked={promotions} onChange={handlePromotionsChange} />
                <label htmlFor="promotions" className="flex items-center gap-1">
                  Promotions
                  <img src="/discount.svg" alt="" className="w-8 h-8" />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {catalogView === 'faceted' ? (
        <FacetedProductCatalog
          searchQuery={searchQuery}
          page={page}
          onChangePage={handlePageChange}
          supplierIds={supplierIdParams}
          productCategoryIds={productCategoryIdParams}
          promotions={promotionsParam}
          onToggleFacetValue={handleToggleFacetValue}
          onClearFacets={handleClearFacets}
          onAddToCart={handleOnAddToCart}
          storeId={storeId}
        />
      ) : (
        <ProductCatalogTable
          searchQuery={searchQuery}
          page={page}
          onChangePage={handlePageChange}
          supplierId={supplierIdParam}
          productCategoryName={productCategoryNameParam}
          promotions={promotionsParam}
          onAddToCart={handleOnAddToCart}
          storeId={storeId}
        />
      )}
    </div>
  );
};

export default Shop;
